import { ButtonGroup } from '@antimatterhq/ui/components/buttonGroup';
import { Label } from '@antimatterhq/ui/components/label';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { useFormikContext } from 'formik';
import { WorkspaceConfigAlertsSchemaValues } from '@/zod/schemas/workspaceConfig/AlertSchema';
import { FormError, FormInput } from '@/components/form/form';
import {
  workspaceConfigServiceNowEventTypeTooltip,
  workspaceConfigServiceNowNodeExprTooltip,
  workspaceConfigServiceNowPasswordTooltip,
  workspaceConfigServiceNowServerTooltip,
  workspaceConfigServiceNowSeverityExprTooltip,
  workspaceConfigServiceNowSourceExprTooltip,
  workspaceConfigServiceNowUserIDTooltip,
} from '@/strings/StringsTooltips';

type ServiceNowAlertProps = {
  formPrefix: 'notables' | 'operational';
  disabled: boolean;
};

export const ServiceNowAlerts = ({
  formPrefix,
  disabled,
}: ServiceNowAlertProps) => {
  const {
    values: baseValues,
    handleBlur,
    handleChange,
  } = useFormikContext<WorkspaceConfigAlertsSchemaValues>();
  const values =
    formPrefix === 'notables' ? baseValues.notables : baseValues.operational;

  return (
    <>
      <Text size="sm">
        {formPrefix === 'notables' ? 'Notables' : 'Operational alerts'} to
        ServiceNow config
      </Text>

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.serviceNowConfig.userID`}
        info={workspaceConfigServiceNowUserIDTooltip}
        required
      >
        ServiceNow user ID
      </Label>
      <FormInput
        type="text"
        name={`${formPrefix}.serviceNowConfig.userID`}
        value={values.serviceNowConfig.userID}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="ServiceNow user ID"
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.serviceNowConfig.userID`} />

      <Spacer size="base" />

      <ButtonGroup
        name={`${formPrefix}.serviceNowConfig.passwordType`}
        value={values.serviceNowConfig.passwordType}
        options={[
          { label: 'Password', value: 'literal' },
          { label: 'Password from secret', value: 'secret' },
        ]}
        onChange={(value) =>
          handleChange({
            target: {
              name: `${formPrefix}.serviceNowConfig.passwordType`,
              value,
            },
          })
        }
        disabled={disabled}
      />

      <Spacer size="base" />

      {values.serviceNowConfig.passwordType === 'literal' && (
        <>
          <Label
            htmlFor={`${formPrefix}.serviceNowConfig.password.value`}
            id={`${formPrefix}.serviceNowConfig.password.value-label`}
            required
            info={workspaceConfigServiceNowPasswordTooltip}
          >
            ServiceNow password
          </Label>
          <FormInput
            aria-labelledby={`${formPrefix}.serviceNowConfig.password.value-label`}
            type="password"
            name={`${formPrefix}.serviceNowConfig.password.value`}
            value={values.serviceNowConfig.password.value}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="ServiceNow password"
            disabled={disabled}
          />
          <FormError name={`${formPrefix}.serviceNowConfig.password.value`} />
        </>
      )}

      {values.serviceNowConfig.passwordType === 'secret' && (
        <>
          <Label
            htmlFor={`${formPrefix}.serviceNowConfig.password.key`}
            id={`${formPrefix}.serviceNowConfig.password.key-label`}
            required
          >
            Databricks secret key
          </Label>
          <FormInput
            aria-labelledby={`${formPrefix}.serviceNowConfig.password.key-label`}
            type="text"
            name={`${formPrefix}.serviceNowConfig.password.key`}
            value={values.serviceNowConfig.password.key}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Secret key"
            disabled={disabled}
          />
          <FormError name={`${formPrefix}.serviceNowConfig.password.key`} />

          <Spacer size="base" />

          <Label
            htmlFor={`${formPrefix}.serviceNowConfig.password.scope`}
            id={`${formPrefix}.serviceNowConfig.password.scope-label`}
            required
          >
            Databricks secret scope
          </Label>
          <FormInput
            aria-labelledby={`${formPrefix}.serviceNowConfig.password.scope-label`}
            type="text"
            name={`${formPrefix}.serviceNowConfig.password.scope`}
            value={values.serviceNowConfig.password.scope}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Secret scope"
            disabled={disabled}
          />
          <FormError name={`${formPrefix}.serviceNowConfig.password.scope`} />
        </>
      )}

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.serviceNowConfig.server`}
        info={workspaceConfigServiceNowServerTooltip}
        required
      >
        ServiceNow server
      </Label>
      <FormInput
        type="text"
        name={`${formPrefix}.serviceNowConfig.server`}
        value={values.serviceNowConfig.server}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="E.g. https://my-instance.service-now.com"
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.serviceNowConfig.server`} />

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.serviceNowConfig.sourceExpr`}
        info={workspaceConfigServiceNowSourceExprTooltip}
      >
        Source expression
      </Label>
      <FormInput
        type="text"
        name={`${formPrefix}.serviceNowConfig.sourceExpr`}
        value={values.serviceNowConfig.sourceExpr}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder=""
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.serviceNowConfig.sourceExpr`} />

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.serviceNowConfig.nodeExpr`}
        info={workspaceConfigServiceNowNodeExprTooltip}
      >
        Node expression
      </Label>
      <FormInput
        type="text"
        name={`${formPrefix}.serviceNowConfig.nodeExpr`}
        value={values.serviceNowConfig.nodeExpr}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder=""
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.serviceNowConfig.nodeExpr`} />

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.serviceNowConfig.severityExpr`}
        info={workspaceConfigServiceNowSeverityExprTooltip}
        required
      >
        Severity expression
      </Label>
      <FormInput
        name={`${formPrefix}.serviceNowConfig.severityExpr`}
        value={values.serviceNowConfig.severityExpr}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="E.g. {severity}"
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.serviceNowConfig.severityExpr`} />

      <Spacer size="base" />

      <Label
        htmlFor={`${formPrefix}.serviceNowConfig.eventType`}
        info={workspaceConfigServiceNowEventTypeTooltip}
      >
        Event type
      </Label>
      <FormInput
        name={`${formPrefix}.serviceNowConfig.eventType`}
        value={values.serviceNowConfig.eventType}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder=""
        disabled={disabled}
      />
      <FormError name={`${formPrefix}.serviceNowConfig.eventType`} />
    </>
  );
};
